import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    output,
    signal,
    Signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { QUEUE_STORE } from '@dxp/core/stores';
import {
    TelXLAvatarComponent,
    TelXLButtonComponent,
    TelXLPanelComponent,
    TelXLSearchComponent,
} from '@dxp/elements';
import { ConversationApiService } from '@dxp/core/api';
import { IConversationQueues, IQueue } from '@dxp/core/models';
import { catchError, distinctUntilChanged, filter, of, switchMap } from 'rxjs';

@Component({
    selector: 'telxl-shared-transfer-queue',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        TelXLPanelComponent,
        TelXLButtonComponent,
        TelXLSearchComponent,
        TelXLAvatarComponent,
    ],
    templateUrl: './transfer-queue.component.html',
    styleUrl: './transfer-queue.component.scss',
})
export class TelXLTransferQueueComponent {
    readonly #queueStore = inject(QUEUE_STORE);
    readonly #conversationApiService = inject(ConversationApiService);

    conversationId = input.required<string>();
    queueId = input.required<string>();
    transfer = output<string>();

    readonly searchText = signal<string>('');

    readonly queue = computed<IQueue | undefined>(() =>
        this.#queueStore.queues().find(q => q.id === this.queueId()),
    );

    readonly #queues: Signal<IConversationQueues[]> = toSignal(
        toObservable(this.conversationId).pipe(
            filter((id): id is string => !!id && id.length > 0),
            distinctUntilChanged(),
            switchMap(id =>
                this.#conversationApiService.getConversationQueues(id),
            ),
            catchError(() => of([] as IConversationQueues[])),
        ),
        { initialValue: [] },
    );

    readonly filteredQueues = computed<IConversationQueues[]>(() =>
        this.#queues().filter(
            q =>
                q.id !== this.queueId() &&
                (this.searchText().length > 0
                    ? q.name
                          .toLowerCase()
                          .includes(this.searchText().toLowerCase())
                    : true),
        ),
    );

    transferToQueue(queueId: string) {
        this.transfer.emit(queueId);
    }

    searchChanged(search: string) {
        this.searchText.set(search);
    }
}
